import { useSyncExternalStore } from 'react';
import { CHART_PALETTE, type ChartColor } from './palette.ts';

export interface ChartTheme {
  /** Gridlines and the axis borders. */
  grid: string;
  /** Axis ticks and legend labels. */
  text: string;
  /** Tooltip background. */
  tooltipSurface: string;
  /** Tooltip body, on that background. */
  tooltipText: string;
  /** Tooltip title, one step quieter than the body. */
  tooltipTitle: string;
  /** Tooltip corner radius, in pixels. */
  tooltipRadius: number;
  fontFamily: string;
  series: Record<ChartColor, string>;
}

/*
 * Last-resort values, used only while no stylesheet has been applied — a chart
 * drawn in an unstyled document should be dull rather than invisible. They are
 * the same tokens written out; theme.css stays the source of truth, and a
 * reading that falls back is never remembered (see `chartTheme`).
 */
const FALLBACK: Record<string, string> = {
  '--color-line': 'rgba(18, 60, 81, 0.13)',
  '--color-ink-muted': 'rgba(18, 60, 81, 0.72)',
  '--color-primary': '#123c51',
  '--color-white': '#ffffff',
  '--color-on-primary-muted': 'rgba(255, 255, 255, 0.55)',
  '--font-sans': 'ui-sans-serif, system-ui, sans-serif',
  '--color-avatar-moss': '#0c7a55',
  '--color-avatar-navy': '#123c51',
  '--color-avatar-teal': '#1f6f87',
  '--color-avatar-plum': '#6b5b95',
  '--color-avatar-berry': '#8a3b73',
  '--color-avatar-ochre': '#9c6420',
  '--radius-sm': '8px',
};

function readChartTheme(style: CSSStyleDeclaration): ChartTheme {
  const token = (name: string): string =>
    style.getPropertyValue(name).trim() || FALLBACK[name] || '';

  const series = Object.fromEntries(
    CHART_PALETTE.map((name) => [name, token(`--color-avatar-${name}`)]),
  ) as Record<ChartColor, string>;

  return {
    grid: token('--color-line'),
    text: token('--color-ink-muted'),
    tooltipSurface: token('--color-primary'),
    tooltipText: token('--color-white'),
    tooltipTitle: token('--color-on-primary-muted'),
    tooltipRadius: Number.parseFloat(token('--radius-sm')) || 8,
    fontFamily: token('--font-sans'),
    series,
  };
}

/* Per theme: dark mode swaps the tokens underneath, so each theme is read once. */
const cached = new Map<string, ChartTheme>();

function themeKey(): string {
  return document.documentElement.dataset.theme ?? 'light';
}

function chartTheme(key: string): ChartTheme {
  const known = cached.get(key);
  if (known) return known;

  const style = getComputedStyle(document.documentElement);
  const theme = readChartTheme(style);

  /*
   * Only remembered once the tokens actually resolve: caching a reading taken
   * before the stylesheet landed would freeze the fallbacks in for the page.
   */
  if (style.getPropertyValue('--color-line').trim() !== '') cached.set(key, theme);

  return theme;
}

/* Re-renders a chart when the app switches theme on <html>. */
function subscribe(onChange: () => void): () => void {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, { attributeFilter: ['data-theme'] });

  return () => observer.disconnect();
}

/**
 * The theme as plain colour strings for chart.js (a canvas has no CSS), read off
 * the root's custom properties so charts follow theme.css and redraw when dark
 * mode turns on or off.
 */
export function useThemeColors(): ChartTheme {
  return chartTheme(useSyncExternalStore(subscribe, themeKey));
}
